import type { ManualOverrideReleasePolicy, PendingManualOverrideAction } from '@/services/inventory'
import { formatDateTimeWithTzShort, isValidTimeZone } from '@/utils'
import { formatOverrideFieldDisplayValue } from '@/utils/overrideFieldDisplay'
import { EDIT_VEHICLE_SPECS_EFFICIENCY_FIELD_LABELS } from '@/constants/vehicleFieldConfig'

export type ManualOverrideDraftDisplayOptions = {
  dealerTimeZone?: string
}

/** UI row for a staged manual override action on a feed-backed vehicle draft. */
export type ManualOverrideDraftDisplayRow = {
  fieldKey: string
  action: 'ADD_OVERRIDE' | 'UPDATE_OVERRIDE' | 'REMOVE_OVERRIDE'
  manualValue?: unknown
  mappedValueAtLock?: unknown
  releasePolicy?: ManualOverrideReleasePolicy
  expiresAt?: string
  sourceFeed?: string
  sourceField?: string
  sourcePath?: string
  changedBy?: string
  updatedAt?: string
}

const ACTION_LABELS: Record<ManualOverrideDraftDisplayRow['action'], string> = {
  ADD_OVERRIDE: 'Add override',
  UPDATE_OVERRIDE: 'Update override',
  REMOVE_OVERRIDE: 'Release override',
}

const RELEASE_POLICY_LABELS: Record<ManualOverrideReleasePolicy, string> = {
  UNTIL_FEED_VALUE_CHANGES: 'Until feed value changes',
  UNTIL_SOLD: 'Until sold',
  MANUAL_RELEASE_ONLY: 'Manual release only',
  UNTIL_DATE: 'Until date',
}

export function formatManualOverrideActionLabel(
  action: ManualOverrideDraftDisplayRow['action'] | string | undefined,
): string {
  const key = String(action || '').trim() as ManualOverrideDraftDisplayRow['action']
  return ACTION_LABELS[key] ?? String(action || 'Override')
}

/** Message shown for a staged REMOVE_OVERRIDE row (release on publish). */
export function formatManualOverrideRemoveMessage(fieldKey: string | undefined): string {
  if (String(fieldKey || '').trim() === 'dealer_photos') {
    return 'This photo override will be released when you publish. The vehicle returns to feed photos.'
  }
  return 'This override will be released when you publish.'
}

const VEHICLE_DRAFT_FIELD_LABELS: Record<string, string> = {
  date_in_stock_ts: 'Date In Stock',
  passenger_capacity: 'Passenger Capacity',
  has_carfax: 'Has CARFAX',
  carfax_one_owner: 'CARFAX One Owner',
  auto_check: 'AutoCheck',
  inventoryState: 'Inventory state',
  inventoryUsageType: 'Inventory usage type',
  websiteVisibility: 'Website visibility',
  websiteVisibilityMode: 'Website visibility mode',
  websiteVisibilityReason: 'Website visibility reason',
  ...EDIT_VEHICLE_SPECS_EFFICIENCY_FIELD_LABELS,
}

export function formatVehicleDraftFieldLabel(fieldKey: string): string {
  const key = String(fieldKey || '').trim()
  if (VEHICLE_DRAFT_FIELD_LABELS[key]) return VEHICLE_DRAFT_FIELD_LABELS[key]

  return key
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export function formatManualOverrideReleasePolicyLabel(
  policy: ManualOverrideReleasePolicy | string | undefined,
  expiresAt?: string,
  dealerTimeZone?: string,
): string {
  const key = String(policy || '').trim() as ManualOverrideReleasePolicy
  const base = RELEASE_POLICY_LABELS[key] ?? String(policy || '—')
  if (key === 'UNTIL_DATE' && expiresAt) {
    const timeZone = String(dealerTimeZone || '').trim()
    const formatted =
      timeZone && isValidTimeZone(timeZone)
        ? formatDateTimeWithTzShort(expiresAt, { timeZone })
        : formatDateTimeWithTzShort(expiresAt)
    return formatted ? `Until ${formatted}` : base
  }
  return base
}

export function formatManualOverrideDraftValue(value: unknown): string {
  if (value === null) return 'null'
  if (value === undefined) return '—'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

export function formatManualOverrideDraftFieldValue(
  fieldKey: string,
  value: unknown,
  options?: ManualOverrideDraftDisplayOptions,
): string {
  if (value === undefined) return '—'

  const normalizedField = String(fieldKey || '').trim()
  if (normalizedField) {
    return formatOverrideFieldDisplayValue(normalizedField, value, {
      dealerTimeZone: options?.dealerTimeZone,
    })
  }

  return formatManualOverrideDraftValue(value)
}

/** Builds sorted display rows from raw draft.manualOverrideActions (history or draft GET). */
export function buildManualOverrideDraftDisplayRows(
  actions: Record<string, PendingManualOverrideAction | unknown> | null | undefined,
): ManualOverrideDraftDisplayRow[] {
  if (!actions || typeof actions !== 'object') return []

  return Object.entries(actions)
    .filter(([fieldKey]) => Boolean(String(fieldKey || '').trim()))
    .map(([fieldKey, raw]) => {
      const entry = (raw && typeof raw === 'object' ? raw : {}) as PendingManualOverrideAction
      const action = String(entry.action || 'ADD_OVERRIDE').trim() as ManualOverrideDraftDisplayRow['action']
      const row: ManualOverrideDraftDisplayRow = {
        fieldKey: String(fieldKey).trim(),
        action:
          action === 'UPDATE_OVERRIDE' || action === 'REMOVE_OVERRIDE' ? action : 'ADD_OVERRIDE',
        changedBy:
          typeof (entry as { changedBy?: { username?: string } }).changedBy?.username === 'string'
            ? String((entry as { changedBy?: { username?: string } }).changedBy?.username)
            : undefined,
        updatedAt: typeof entry.updatedAt === 'string' ? entry.updatedAt : undefined,
      }

      if (row.action === 'REMOVE_OVERRIDE') {
        return row
      }

      row.manualValue = entry.manualValue
      row.mappedValueAtLock = entry.mappedValueAtLock
      row.releasePolicy = entry.releasePolicy
      row.expiresAt = entry.expiresAt
      row.sourceFeed = entry.sourceFeed
      row.sourceField = entry.sourceField
      row.sourcePath = entry.sourcePath
      return row
    })
    .sort((a, b) => a.fieldKey.localeCompare(b.fieldKey))
}

export function countDraftVisibleChanges(draft: {
  fieldKeys?: string[]
  operationalFieldKeys?: string[]
  manualOverrideActionKeys?: string[]
}): number {
  const fieldCount = Array.isArray(draft?.fieldKeys) ? draft.fieldKeys.length : 0
  const operationalCount = Array.isArray(draft?.operationalFieldKeys)
    ? draft.operationalFieldKeys.length
    : 0
  const manualOverrideCount = Array.isArray(draft?.manualOverrideActionKeys)
    ? draft.manualOverrideActionKeys.length
    : 0
  return fieldCount + operationalCount + manualOverrideCount
}

export function draftHasVisibleChanges(draft: {
  fieldKeys?: string[]
  operationalFieldKeys?: string[]
  manualOverrideActionKeys?: string[]
}): boolean {
  return countDraftVisibleChanges(draft) > 0
}
